import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { ScrollButton } from 'components/FiltersPopovers/FiltersScrollWrapper/ScrollButton';
import { useElementSize } from 'hooks/useElementSize';
import throttle from 'lodash/throttle';
import { getIsRtlLanguage } from 'modules/meta/selectors';
import { Spacings } from 'types/Theme';
import { Flex } from '@daedalus/atlas/helpers/Flex';
const SwiperContainer = /*#__PURE__*/styled('div')({
  name: "SwiperContainer",
  class: "svj9imj",
  propsAsIs: false
});
const _exp = /*#__PURE__*/() => Flex.Row;
const SwiperWrapper = /*#__PURE__*/styled(_exp())({
  name: "SwiperWrapper",
  class: "savh14b",
  propsAsIs: true
});
const THROTTLING_INTERVAL = 500; // ms

type ActionType = 'NEXT' | 'PREV';
interface Props {
  // Elements that will be rendered inside the scroll area as slides
  children: React.ReactNode;
  // Gap between slides
  gap: keyof Spacings;
  // Left margin for the left arrow button
  left?: number;
  // Right margin for the right arrow button
  right?: number;
  // Threshold for scroll position
  threshold?: number;
  // Shadow width
  shadowWidth?: number;
}
export const Swiper: React.FC<Props> = ({
  children,
  gap,
  left = 0,
  right = 0,
  threshold = 200,
  shadowWidth = 120
}) => {
  const isRtl = useSelector(getIsRtlLanguage);
  const {
    elementRef,
    size: {
      width: wrapperWidth,
      el: wrapperElement
    }
  } = useElementSize<HTMLDivElement>();
  const [scrollableOffset, setScrollableOffset] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  useEffect(() => {
    if (wrapperElement) {
      const roundedWrapperWidth = Math.ceil(wrapperWidth);
      const newScrollableOffset = roundedWrapperWidth > 0 ? Math.ceil(wrapperElement.scrollWidth) - roundedWrapperWidth : 0;
      if (scrollableOffset !== newScrollableOffset) {
        setScrollableOffset(Math.round(newScrollableOffset));
      }
    }
  }, [scrollableOffset, wrapperWidth, scrollPosition, wrapperElement]);
  useEffect(() => {
    const ref = wrapperElement;
    if (ref) {
      setScrollWidth(Math.ceil(ref.scrollWidth));
      const onScroll = throttle(() => {
        setScrollPosition(Math.round(ref.scrollLeft));
      }, THROTTLING_INTERVAL);
      ref.addEventListener('scroll', onScroll);
      return () => ref.removeEventListener('scroll', onScroll);
    }
  }, [wrapperElement]);
  const getWidthToScroll = useCallback((direction: ActionType) => {
    const realScrollPosition = isRtl ? -scrollPosition : scrollPosition;
    if (direction === 'NEXT') {
      if (scrollWidth >= realScrollPosition + wrapperWidth) return Math.ceil(realScrollPosition + wrapperWidth) - threshold;
      return scrollableOffset;
    }
    if (realScrollPosition + threshold >= wrapperWidth) return Math.ceil(realScrollPosition - wrapperWidth + threshold);
    return 0;
  }, [isRtl, scrollPosition, scrollWidth, scrollableOffset, wrapperWidth, threshold]);
  const isButtonVisible = useCallback((direction: ActionType): boolean => {
    if (isRtl) {
      if (direction === 'NEXT') return -scrollPosition < scrollableOffset;
      return scrollPosition < 0;
    }
    if (direction === 'NEXT') return scrollPosition < scrollableOffset;
    return scrollPosition > 0;
  }, [isRtl, scrollPosition, scrollableOffset]);
  const onArrowClick = useCallback((direction: ActionType) => {
    if (!isButtonVisible(direction)) return;
    const widthToScroll = getWidthToScroll(direction);
    wrapperElement.scroll({
      left: (isRtl ? -1 : 1) * widthToScroll,
      behavior: 'smooth'
    });
  }, [wrapperElement, getWidthToScroll, isRtl, isButtonVisible]);
  return <SwiperContainer>
      <SwiperWrapper gap={gap} componentRef={elementRef}>
        {children}
      </SwiperWrapper>
      <ScrollButton direction="left" isVisible={isButtonVisible('PREV')} onClick={() => onArrowClick('PREV')} marginLeft="s000" paddingLeft="s300" shadowWidth={shadowWidth} left={left} isRtl={isRtl} />
      <ScrollButton direction="right" isVisible={isButtonVisible('NEXT')} onClick={() => onArrowClick('NEXT')} marginRight="s000" paddingRight="s300" shadowWidth={shadowWidth} right={right} isRtl={isRtl} />
    </SwiperContainer>;
};

require("./Swiper.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Swiper.tsx");